import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneRight, Spinner, Brain, User } from "@phosphor-icons/react";

const quickPrompts = [
  "Suggest a 4-day push/pull/legs split",
  "How to improve my bench press?",
  "Best exercises for building back width",
  "Recovery tips for sore muscles",
  "Create a beginner full-body routine",
];

export default function AICoach({ api }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;

    const userMsg = { role: "user", content: msg };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${api}/ai/suggest`, { message: msg, context: "general" });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (e) {
      toast.error("AI Coach unavailable");
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, I'm having trouble right now. Try again shortly." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="ai-coach-page">
      <div className="mb-4">
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          AI Coach
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Powered by GPT-5.2 — your personal fitness advisor</p>
      </div>

      {/* Messages Area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-4 pr-2"
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="ai-glow bg-[#161616] border border-[#27272A] p-8 max-w-lg text-center" data-testid="ai-welcome">
              <Brain size={48} className="text-[#E05A1D] mx-auto mb-4" weight="duotone" />
              <h3
                className="text-xl font-bold uppercase tracking-tight text-white mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Your AI Fitness Coach
              </h3>
              <p className="text-sm text-[#A1A1AA] mb-6">
                Ask me about workout routines, exercise form, nutrition tips, or training advice.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {quickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(prompt)}
                    className="text-xs bg-[#0F0F0F] border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#E05A1D] px-3 py-2 transition-all"
                    data-testid={`quick-prompt-${i}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-message-${i}`}
          >
            {msg.role === "assistant" && (
              <div className="w-8 h-8 bg-[#E05A1D]/10 border border-[#E05A1D]/30 flex items-center justify-center shrink-0">
                <Brain size={16} className="text-[#E05A1D]" weight="fill" />
              </div>
            )}
            <div
              className={`max-w-[70%] p-4 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-[#E05A1D] text-white"
                  : "bg-[#161616] border border-[#27272A] text-[#E5E5E5]"
              }`}
            >
              <pre className="whitespace-pre-wrap font-[inherit]">{msg.content}</pre>
            </div>
            {msg.role === "user" && (
              <div className="w-8 h-8 bg-[#27272A] flex items-center justify-center shrink-0">
                <User size={16} className="text-[#A1A1AA]" weight="fill" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3" data-testid="ai-loading">
            <div className="w-8 h-8 bg-[#E05A1D]/10 border border-[#E05A1D]/30 flex items-center justify-center">
              <Brain size={16} className="text-[#E05A1D]" weight="fill" />
            </div>
            <div className="bg-[#161616] border border-[#27272A] p-4 flex items-center gap-2">
              <Spinner size={16} className="animate-spin text-[#E05A1D]" />
              <span className="text-sm text-[#71717A]">Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="mt-4 flex gap-3" data-testid="chat-input-area">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-[#0F0F0F] border border-[#27272A] text-white px-4 py-3 text-sm focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
          placeholder="Ask your AI coach anything..."
          disabled={loading}
          data-testid="chat-input"
        />
        <button
          onClick={() => sendMessage()}
          disabled={loading || !input.trim()}
          className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white px-5 py-3 transition-colors disabled:opacity-50 flex items-center gap-2 font-bold uppercase tracking-wide text-sm"
          data-testid="chat-send-btn"
        >
          <PaperPlaneRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
